import React from "react";
import { Formik, FormikHelpers } from 'formik';
import { Alert, Box, Button, Grid, Group } from "@mantine/core";
import { useNotifications } from "@mantine/notifications";
import { Icon } from "@iconify/react";

import { buildFormConfig } from "utils/forms";

import { GenericDictionary, GenericError } from "state/_types";

import Icons from "components/ui/Icons";
import FieldRenderer from "components/forms/FieldRenderer";

type WizardStepProps<T extends GenericDictionary> = {
    formSchema: any;
    updateAction?(value: any): Promise<any>;
    resetAction?(): void;
    isLoading: boolean;
    errors: GenericError[];
    currentItemData: T;
    actionColor?: string;
    requiredDirty?: boolean;
    validators?: GenericDictionary;
}

function WizardStep<PayloadType extends GenericDictionary>({
    formSchema,
    updateAction,
    resetAction,
    errors,
    currentItemData,
    requiredDirty = false,
    validators = {}
}: React.PropsWithChildren<WizardStepProps<PayloadType>>): React.ReactElement | null {

    const notifications = useNotifications();
    const formMode = 'update'
    const [initialState] = React.useState<PayloadType>(currentItemData);
    const formConfig = buildFormConfig(formSchema, validators, 'update')

    // React.useEffect(() => {
    //     setFormMode('update');
    //     let config = buildFormConfig(formSchema, validators, 'update')
    //     setFormConfig(config)
    // }, []);

    const getEditablePayload = (payload: PayloadType): PayloadType => {
        let editablePayload: GenericDictionary = {};
        Object.keys(payload).forEach((payloadKey) => {
            if (formConfig && formConfig.editable.indexOf(payloadKey) > -1) {
                editablePayload[payloadKey] = payload[payloadKey]
            }
        });
        return editablePayload as PayloadType;
    }

    const onSubmit = async (payload: PayloadType, { resetForm }: FormikHelpers<PayloadType>) => {
        let success = false;
        if (formMode === 'update' && updateAction) {
            let editablePayload = getEditablePayload(payload);
            success = await updateAction(editablePayload);
            if (success) {
                if (resetAction) resetAction();
            } else {
                notifications.showNotification({
                    icon: <Icon icon={Icons.warningCircle} width={24} />,
                    color: 'red',
                    autoClose: 1000,
                    title: 'Opps!',
                    message: `Could Not Update ${formConfig?.title || 'Item'}`
                })
            }
        }
    }

    const displayErrors = () => {
        if (!errors.length) return null;
        return (
            <Alert title="Error" variant="outline" m={7} color="red">
                {errors.map((error, key) => {
                    return (
                        <span key={`error-${key}`}>{error.text}</span>
                    )
                })}
            </Alert>
        )
    }

    function onKeyDown(keyEvent: any) {
        if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
            keyEvent.preventDefault();
        }
    }

    if (!formMode || !formConfig) return null;

    return (
        <React.Fragment>

            <Formik
                onKeyDown={onKeyDown}
                initialValues={initialState}
                onSubmit={onSubmit}
                validationSchema={formConfig.validationSchema}
                validateOnBlur={true}
                validateOnMount={false}
                validateOnChange={true}
            >
                {({ values, errors, handleChange, handleBlur, handleSubmit, submitForm, isSubmitting, isValid, dirty, setFieldValue }) => {

                    return (

                        <Box
                            component="form"
                            onSubmit={handleSubmit}
                        >


                            <Grid gutter="xs" sx={{ margin: 0 }}>

                                <FieldRenderer
                                    currentItemId={null}
                                    formConfig={formConfig}
                                    formMode={formMode}
                                    values={values}
                                    errors={errors}
                                    setFieldValue={setFieldValue}
                                    handleBlur={handleBlur}
                                    handleChange={handleChange}
                                />

                            </Grid>

                            <Box>
                                {displayErrors()}
                            </Box>

                            <Box>
                                <Group position="right">
                                    <Group >
                                        <Button
                                            color="green"
                                            size="lg"
                                            onClick={submitForm}
                                            loading={isSubmitting}
                                            disabled={requiredDirty ? !dirty || !isValid : !isValid}
                                        >
                                            Next
                                        </Button>
                                    </Group>
                                </Group>
                            </Box>

                        </Box>

                    )
                }}

            </Formik>

        </React.Fragment>
    )

}

export default WizardStep;